export {};

const LAST_VIEWED_PRODUCT_KEY = 'lastViewedProduct';
const LAST_VIEWED_PRODUCT_ID_KEY = 'lastViewedProductId';
const LAST_VIEWED_PRODUCT_HANDLE_KEY = 'lastViewedProductHandle';
const SCROLL_TOLERANCE = 2;
const MIN_SCROLLER_THUMB_WIDTH = 14;

/**
 * @returns {{ id: string | null, handle: string | null }}
 */
const getLastViewedProduct = () => {
  try {
    const storedProduct = localStorage.getItem(LAST_VIEWED_PRODUCT_KEY);
    if (storedProduct) {
      const parsedProduct = JSON.parse(storedProduct);
      if (parsedProduct && typeof parsedProduct === 'object') {
        return {
          id: parsedProduct.id != null ? String(parsedProduct.id) : null,
          handle: typeof parsedProduct.handle === 'string' && parsedProduct.handle ? parsedProduct.handle : null,
        };
      }
    }

    return {
      id: localStorage.getItem(LAST_VIEWED_PRODUCT_ID_KEY),
      handle: localStorage.getItem(LAST_VIEWED_PRODUCT_HANDLE_KEY),
    };
  } catch (error) {
    console.warn('Failed to read last viewed product for empty cart recommendations', error);
    return { id: null, handle: null };
  }
};

/**
 * @param {string} sectionId
 * @param {{ handle: string | null, id: string | null }} lastViewedProduct
 * @param {string | undefined} baseUrl
 * @param {string | undefined} intent
 * @returns {string | null}
 */
const buildRequestUrl = (sectionId, lastViewedProduct, baseUrl, intent) => {
  const shopifyConfig = /** @type {{ routes?: { root?: string } } | undefined} */ (window.Shopify);
  const rootPath = shopifyConfig?.routes?.root || '/';

  if (lastViewedProduct.handle) {
    const url = new URL(`${rootPath}products/${encodeURIComponent(lastViewedProduct.handle)}`, window.location.origin);
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  }

  if (lastViewedProduct.id && baseUrl) {
    const url = new URL(baseUrl, window.location.origin);
    url.searchParams.set('product_id', lastViewedProduct.id);
    url.searchParams.set('section_id', sectionId);
    url.searchParams.set('intent', intent || 'related');
    return url.toString();
  }

  return null;
};

class EmptyCartRecommendations extends HTMLElement {
  static #responseCache = new Map();

  #abortController = null;
  #cartDrawer = null;
  #requestToken = 0;
  #scrollerCleanup = null;

  connectedCallback() {
    this.#cartDrawer = this.closest('cart-drawer-component');
    this.#cartDrawer?.addEventListener('dialog:open', this.#handleDrawerOpen);
    this.#loadRecommendations();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
    this.#abortController = null;
    this.#scrollerCleanup?.();
    this.#scrollerCleanup = null;
    this.#cartDrawer?.removeEventListener('dialog:open', this.#handleDrawerOpen);
    this.#cartDrawer = null;
  }

  #handleDrawerOpen = () => {
    this.#loadRecommendations();
  };

  async #loadRecommendations() {
    const content = this.querySelector('[data-empty-cart-recommendations-content]');
    const baseUrl = this.dataset.url;
    const sectionId = this.dataset.sectionId;
    const lastViewedProduct = getLastViewedProduct();

    if (!(content instanceof HTMLElement) || !sectionId) return;

    this.#setupScroller(content);

    console.debug('Empty cart related products state', {
      emptyStateRendered: true,
      productId: lastViewedProduct.id,
      productHandle: lastViewedProduct.handle,
    });

    const requestUrl = buildRequestUrl(sectionId, lastViewedProduct, baseUrl, this.dataset.intent);
    if (!requestUrl) {
      this.#setLoadingState(content, false);
      console.debug('Empty cart related products fallback', {
        reason: 'missing_last_viewed_product',
      });
      return;
    }

    this.#abortController?.abort();
    this.#abortController = new AbortController();

    const cacheKey = requestUrl;
    const cachedMarkup = EmptyCartRecommendations.#responseCache.get(cacheKey);
    const requestToken = ++this.#requestToken;
    this.#setLoadingState(content, true);

    try {
      let markup = cachedMarkup;
      if (!markup) {
        const response = await fetch(cacheKey, {
          credentials: 'same-origin',
          signal: this.#abortController.signal,
        });

        if (!response.ok) {
          if (requestToken === this.#requestToken) this.#setLoadingState(content, false);
          return;
        }

        markup = await response.text();
        EmptyCartRecommendations.#responseCache.set(cacheKey, markup);
      }

      console.debug('Empty cart recommendations API response', {
        productId: lastViewedProduct.id,
        productHandle: lastViewedProduct.handle,
        url: cacheKey,
        response: markup,
      });

      const parsedDocument = new DOMParser().parseFromString(markup, 'text/html');
      const nextContent = parsedDocument.querySelector('[data-empty-cart-recommendations-content]');
      if (!(nextContent instanceof HTMLElement)) {
        if (requestToken === this.#requestToken) this.#setLoadingState(content, false);
        return;
      }

      const hasContent = nextContent.innerHTML.trim().length > 0;
      if (!hasContent) {
        if (requestToken === this.#requestToken) this.#setLoadingState(content, false);
        return;
      }

      content.innerHTML = nextContent.innerHTML;
      this.#setupScroller(content);
      this.hidden = false;
      if (requestToken === this.#requestToken) this.#setLoadingState(content, false);
    } catch (error) {
      if (error instanceof DOMException && error.name === 'AbortError') {
        if (requestToken === this.#requestToken) this.#setLoadingState(content, false);
        return;
      }
      console.warn('Failed to load empty cart recommendations', error);
      if (requestToken === this.#requestToken) this.#setLoadingState(content, false);
    } finally {
      this.#abortController = null;
    }
  }

  /**
   * @param {HTMLElement} content
   * @param {boolean} isLoading
   * @returns {void}
   */
  #setLoadingState(content, isLoading) {
    content.toggleAttribute('data-empty-cart-loading', isLoading);
  }

  /**
   * @param {HTMLElement} content
   * @returns {void}
   */
  #setupScroller(content) {
    this.#scrollerCleanup?.();
    this.#scrollerCleanup = null;

    const grid = content.querySelector('.cart-items__empty-recommendations-grid');
    const controls = content.querySelector('[data-empty-cart-recommendations-controls]');
    const scroller = content.querySelector('[data-empty-cart-recommendations-scroller]');
    const thumb = content.querySelector('[data-empty-cart-recommendations-scroller-thumb]');
    const prevButton = content.querySelector('[data-empty-cart-recommendations-prev]');
    const nextButton = content.querySelector('[data-empty-cart-recommendations-next]');

    if (!(grid instanceof HTMLElement)) return;
    if (!(controls instanceof HTMLElement)) return;
    if (!(scroller instanceof HTMLElement)) return;
    if (!(thumb instanceof HTMLElement)) return;
    if (!(prevButton instanceof HTMLButtonElement)) return;
    if (!(nextButton instanceof HTMLButtonElement)) return;

    const getScrollStep = () => {
      const firstCard = grid.querySelector('.cart-items__empty-recommendation');
      if (firstCard instanceof HTMLElement) {
        const styles = window.getComputedStyle(grid);
        const gap = Number.parseFloat(styles.columnGap || styles.gap || '0') || 0;
        return firstCard.getBoundingClientRect().width + gap;
      }

      return Math.max(grid.clientWidth * 0.85, 1);
    };

    const updateScroller = () => {
      const maxScrollLeft = Math.max(0, grid.scrollWidth - grid.clientWidth);
      const hasOverflow = maxScrollLeft > SCROLL_TOLERANCE;

      controls.hidden = !hasOverflow;
      if (!hasOverflow) {
        prevButton.disabled = true;
        nextButton.disabled = true;
        thumb.style.setProperty('--empty-cart-scroller-thumb-width', `${scroller.clientWidth || 64}px`);
        thumb.style.setProperty('--empty-cart-scroller-thumb-translate', '0px');
        return;
      }

      const trackWidth = scroller.getBoundingClientRect().width || 64;
      const thumbWidth = Math.max(
        Math.min((grid.clientWidth / grid.scrollWidth) * trackWidth, trackWidth),
        MIN_SCROLLER_THUMB_WIDTH
      );
      const maxTranslate = Math.max(0, trackWidth - thumbWidth);
      const progress = maxScrollLeft > 0 ? grid.scrollLeft / maxScrollLeft : 0;

      thumb.style.setProperty('--empty-cart-scroller-thumb-width', `${thumbWidth}px`);
      thumb.style.setProperty('--empty-cart-scroller-thumb-translate', `${maxTranslate * progress}px`);
      prevButton.disabled = grid.scrollLeft <= SCROLL_TOLERANCE;
      nextButton.disabled = grid.scrollLeft >= maxScrollLeft - SCROLL_TOLERANCE;
    };

    const onPrevClick = () => {
      grid.scrollBy({ left: -getScrollStep(), behavior: 'smooth' });
    };

    const onNextClick = () => {
      grid.scrollBy({ left: getScrollStep(), behavior: 'smooth' });
    };

    updateScroller();

    const resizeObserver = new ResizeObserver(updateScroller);
    resizeObserver.observe(grid);
    resizeObserver.observe(scroller);
    prevButton.addEventListener('click', onPrevClick);
    nextButton.addEventListener('click', onNextClick);
    grid.addEventListener('scroll', updateScroller, { passive: true });

    this.#scrollerCleanup = () => {
      prevButton.removeEventListener('click', onPrevClick);
      nextButton.removeEventListener('click', onNextClick);
      grid.removeEventListener('scroll', updateScroller);
      resizeObserver.disconnect();
    };
  }
}

if (!customElements.get('empty-cart-recommendations')) {
  customElements.define('empty-cart-recommendations', EmptyCartRecommendations);
}
